import { useEffect, useState } from "react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "@/lib/api";
import { money } from "@/lib/odds";
import { UpgradeGate } from "@/components/TierBadge";
import { Badges } from "@/components/Badges";
import { BankrollPanel } from "@/components/BankrollPanel";
import { ModelAnalytics } from "@/components/ModelAnalytics";
import { WeeklyRecap } from "@/components/WeeklyRecap";
import { RealAccountPanel } from "@/components/RealAccountPanel";

const tooltipStyle = { background: "#121824", border: "1px solid #26334D", borderRadius: 8, fontSize: 12 };

export default function Performance() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/performance").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="mx-auto max-w-7xl px-4 py-10"><div className="glass-card h-40 animate-pulse" /></div>;

  return (
    <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="performance-dashboard">
      <p className="label-caps text-emerald-400">Results tracking</p>
      <h1 className="font-display mt-2 text-4xl font-black uppercase tracking-tight text-white sm:text-5xl">Performance</h1>

      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Metric id="metric-win-rate" label="Win rate" value={`${d.win_rate}%`} sub={`${d.won}W – ${d.lost}L · ${d.pending} pending`} color="text-emerald-400" />
        <Metric id="metric-total-roi" label="ROI" value={`${d.roi >= 0 ? "+" : ""}${d.roi}%`} sub={`on ${money(d.total_wagered)} wagered`} color={d.roi >= 0 ? "text-emerald-400" : "text-red-400"} />
        <Metric id="metric-net-profit" label="Net profit" value={`${d.net_profit >= 0 ? "+" : ""}${money(d.net_profit)}`} sub={`${d.settled} settled parlays`} color={d.net_profit >= 0 ? "text-emerald-400" : "text-red-400"} />
        <Metric id="metric-biggest-win" label="Biggest win" value={`+${money(d.biggest_win)}`} sub="single parlay profit" color="text-amber-400" />
      </div>
      {d.badges?.length > 0 && (
        <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="performance-badges">
          <span className="label-caps">Your badges</span>
          <Badges badges={d.badges} size="lg" />
        </div>
      )}

      <div className="mt-6"><WeeklyRecap /></div>
      <div className="mt-6"><RealAccountPanel real={d.real} modelProfit={d.net_profit} /></div>
      <div className="mt-6"><BankrollPanel netProfit={d.net_profit} /></div>
      <div className="mt-6"><ModelAnalytics /></div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="glass-card p-6 lg:col-span-2" data-testid="chart-roi-history">
          <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-white">Cumulative profit</h2>
          {d.history.length === 0 ? <p className="mt-6 text-sm text-slate-500">Settle a parlay to start your curve.</p> : (
            <div className="mt-4 h-64">
              <ResponsiveContainer>
                <AreaChart data={d.history}>
                  <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10B981" stopOpacity={0.4} /><stop offset="100%" stopColor="#10B981" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid stroke="#26334D" strokeDasharray="3 3" />
                  <XAxis dataKey="date" stroke="#64748B" fontSize={11} tickFormatter={(v) => v.slice(5)} />
                  <YAxis stroke="#64748B" fontSize={11} tickFormatter={(v) => `$${v}`} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => [money(v), "Profit"]} />
                  <Area type="monotone" dataKey="profit" stroke="#10B981" strokeWidth={2} fill="url(#g)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
        <div className="glass-card p-6" data-testid="chart-by-sport">
          <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-white">Leg hit rate by sport</h2>
          {d.by_sport.length === 0 ? <p className="mt-6 text-sm text-slate-500">No settled legs yet.</p> : (
            <div className="mt-4 h-64">
              <ResponsiveContainer>
                <BarChart data={d.by_sport} layout="vertical" margin={{ left: 10 }}>
                  <XAxis type="number" domain={[0, 100]} hide />
                  <YAxis type="category" dataKey="label" stroke="#94A3B8" fontSize={11} width={80} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v}%`, "Hit rate"]} />
                  <Bar dataKey="hit_rate" fill="#06B6D4" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6">
        {d.ai_accuracy ? (
          <div className="glass-card p-6" data-testid="ai-accuracy-panel">
            <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-white">AI model accuracy by confidence tier</h2>
            <p className="text-xs text-slate-500">Hit rate of all settled Parlay Lab picks, grouped by the model's stated confidence.</p>
            <div className="mt-5 grid gap-4 sm:grid-cols-3">
              {d.ai_accuracy.map((a) => (
                <div key={a.confidence} className="rounded-xl border border-[#26334D] bg-[#0B0E14]/60 p-4">
                  <p className="label-caps">{a.confidence} confidence</p>
                  <p className="font-mono-num mt-1 text-3xl font-bold text-white">{a.hit_rate}%</p>
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#26334D]"><div className="h-full rounded-full bg-emerald-400 transition-[width] duration-700" style={{ width: `${a.hit_rate}%` }} /></div>
                  <p className="mt-1 text-xs text-slate-500">{a.won} / {a.total} picks</p>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <UpgradeGate testId="analytics-upgrade-gate" title="Detailed analytics" description="Premium unlocks AI model accuracy by confidence tier, deeper breakdowns and the full analytics suite." />
        )}
      </div>
    </main>
  );
}

function Metric({ id, label, value, sub, color }) {
  return (
    <div className="glass-card p-5 fade-up" data-testid={id}>
      <p className="label-caps">{label}</p>
      <p className={`font-mono-num mt-2 text-3xl font-bold ${color}`}>{value}</p>
      <p className="mt-1 text-xs text-slate-500">{sub}</p>
    </div>
  );
}
